// src/pages/LandingPage.tsx
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrivacyPolicy from '../components/PrivacyPolicy';
import TermsOfService from '../components/TermsOfService';
import { useAuth } from '../contexts/AuthContext';
import CookieConsent from '../components/CookieConsent';
import LeadFormSection from '../components/LeadFormSection';
import SystemShowcase from '../components/SystemShowcase';
import {
  Users, Briefcase, Brain, FileText, ChevronRight, Star, TrendingUp, Shield,
  CheckCircle, Lock, Award, Gift, BarChart3, Calculator, GraduationCap,
  PieChart, Target, Zap, Layers, Download, Puzzle, ArrowRight, MessageSquare, Wallet,
} from 'lucide-react';

// ================= КОМПОНЕНТ ВРАЩАЮЩЕЙСЯ ЛАМЫ =================
const SpinningLlama: React.FC<{ size?: number }> = ({ size = 200 }) => (
  <div style={{ width: size, height: size, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
    <style>{`@keyframes spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '50%', border: '4px solid transparent', borderTopColor: '#6a11cb', borderRightColor: '#2575fc', borderBottomColor: '#6a11cb', borderLeftColor: '#2575fc', animation: 'spin 1.2s linear infinite', boxShadow: '0 0 25px rgba(37,117,252,0.5)' }} />
    <img src="/images/load.png" alt="Лама" style={{ width: size * 0.7, height: size * 0.7, objectFit: 'contain', zIndex: 1, filter: 'drop-shadow(0 0 15px rgba(106,17,203,0.3))' }} />
  </div>
);

// ================= АНИМАЦИОННЫЕ ВАРИАНТЫ =================
const fadeUp: any = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};
const stagger: any = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.05 } }
};
const scaleIn: any = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const LandingPage: React.FC = () => {
  useSEO();

  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Скрываем JivoChat при открытии модалки мобильного приложения
  useEffect(() => {
    const toggleJivoVisibility = () => {
      const jivoElements = document.querySelectorAll('jdiv');
      jivoElements.forEach(el => {
        if (el.parentNode === document.body || el.id?.includes('jivo')) {
          (el as HTMLElement).style.display = appModalOpen ? 'none' : '';
        }
      });
    };
    toggleJivoVisibility();
    const timer = setTimeout(toggleJivoVisibility, 500);
    return () => clearTimeout(timer);
  }, [appModalOpen]);

  // Оптимизированный параллакс
  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => setScrollY(window.scrollY));
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Глобальные стили
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      html, body, #root { margin: 0 !important; padding: 0 !important; height: 100% !important; width: 100% !important; }
      html { overflow-x: hidden; overflow-y: auto; overscroll-behavior-y: none; scroll-behavior: smooth; }
      body { overflow-x: hidden; overflow-y: visible; -webkit-overflow-scrolling: touch; }
      * { box-sizing: border-box; }
      @keyframes floaty { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const features = [
    { title: 'HRM', description: 'Сотрудники, отпуска, приказы, ЭЦП.', icon: <Users size={32} />, color: '#2575fc', action: () => (user ? navigate('/app/hr') : navigate('/register')) },
    { title: 'Рекрутинг', description: 'Вакансии, кандидаты, воронка найма, тесты.', icon: <Briefcase size={32} />, color: '#6a11cb', action: () => (user ? navigate('/app/hr/vacancies') : navigate('/register')) },
    { title: 'КДП', description: 'Приказы, ЭДО, шаблоны, архив.', icon: <FileText size={32} />, color: '#28a745', action: () => (user ? navigate('/app/hr-documents') : navigate('/register')) },
    { title: 'SalesCRM', description: 'Воронки продаж, сделки, задачи, документы, встречи.', icon: <TrendingUp size={32} />, color: '#e83e8c', action: () => (user ? navigate('/app/sales/deals') : navigate('/register')) },
    { title: 'Бухгалтерия', description: 'Счета, контрагенты, интеграция с 1С.', icon: <Calculator size={32} />, color: '#ffc107', action: () => (user ? navigate('/app/accounting') : navigate('/register')) },
    { title: 'Аналитика', description: 'BI-дашборды, KPI, HR-метрики.', icon: <BarChart3 size={32} />, color: '#17a2b8', action: () => (user ? navigate('/app/bi') : navigate('/register')) },
  ];

  const stats = [
    { value: '6', label: 'направлений в одной платформе', icon: <Layers size={20} /> },
    { value: '50+', label: 'тестов и опросников', icon: <Brain size={20} /> },
    { value: '100+', label: 'готовых инструментов', icon: <Zap size={20} /> },
    { value: '∞', label: 'без ограничений', icon: <Star size={20} /> },
  ];

  const testimonials = [
    { text: 'Платформа Лама HR помогла нам сократить время закрытия вакансий на 40%. Канбан-доска – просто находка!', name: 'Валерия Селезнева', role: '"Индивидуальный предприниматель"' },
    { text: 'Благодаря аналитике и тестам мы подобрали идеальных кандидатов на ключевые позиции. Спасибо разработчикам!', name: 'Евгения Наймушина', role: '"Индивидуальный предприниматель"' },
  ];

  const pricing = {
    monthly: { price: 2000, period: 'месяц', label: 'Помесячно', color: '#2575fc', description: 'Гибкий старт без обязательств' },
    quarterly: { price: 4500, period: 'квартал', label: 'Квартал', color: '#28a745', description: 'Оптимальный баланс цены и срока' },
    yearly: { price: 15000, pricePerMonth: Math.round(15000 / 12), period: 'год', label: 'Годовая', color: '#6a11cb', saving: 9000, description: 'Максимальная выгода для команды' },
  };

  return (
    <div style={styles.container}>
      {/* Скрытый H1 */}
      <h1 style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
        HR LAMA — российская HR-экосистема для управления персоналом и рекрутинга
      </h1>

      {/* Параллакс-слои */}
      <div style={{ ...styles.parallaxLayer, transform: `translate3d(0, ${scrollY * 0.15}px, 0)`, background: 'radial-gradient(circle, rgba(106,17,203,0.18) 0%, transparent 70%)', top: '-10%', left: '-5%', width: '40vw', height: '40vw' }} />
      <div style={{ ...styles.parallaxLayer, transform: `translate3d(0, ${scrollY * -0.25}px, 0)`, background: 'radial-gradient(circle, rgba(37,117,252,0.15) 0%, transparent 70%)', bottom: '10%', right: '0%', width: '50vw', height: '50vw' }} />

      {/* ===== HEADER ===== */}
      <header
        style={{ ...styles.header, boxShadow: scrollY > 20 ? '0 8px 30px rgba(0,0,0,0.08)' : '0 1px 10px rgba(0,0,0,0.05)' }}
      >
        <div style={styles.logoContainer}>
          <img src="/images/14.png" alt="Лама" style={styles.logoIcon} />
        </div>
        <div>
          {user ? (
            <button onClick={() => navigate('/dashboard')} style={styles.buttonPrimary}>Перейти в дашборд</button>
          ) : (
            <>
              <button onClick={() => navigate('/login')} style={styles.buttonSecondary}>Войти</button>
              <button onClick={() => navigate('/register')} style={styles.buttonPrimary}>Регистрация</button>
            </>
          )}
        </div>
      </header>

      <main style={styles.main}>
        {/* ===== HERO ===== */}
        <section style={styles.hero}>
          <div style={styles.heroContent}>
            <div style={styles.heroBadge}>
              <Zap size={16} /><span>Единая экосистема: 6 модулей в одном окне</span>
            </div>
            <h1 style={styles.heroTitle}>
              Всё для HR и продаж<br /><span style={styles.gradientText}>в одном окне</span>
            </h1>
            <p style={styles.heroSubtitle}>
              HR LAMA — российская экосистема для работы с людьми и деньгами.
              Кадры, найм, документы, сделки и финансы связаны в едином пространстве —
              данные не теряются, процессы не рвутся.
            </p>
            {!user && (
              <>
                <div style={styles.heroButtons}>
                  <button onClick={() => navigate('/register')} style={styles.ctaButton}>
                    Начать бесплатно <ChevronRight size={18} style={{ marginLeft: '8px' }} />
                  </button>
                  <button onClick={() => navigate('/login')} style={styles.ctaOutlineButton}>
                    Демо‑доступ
                  </button>
                </div>
                <div style={styles.demoNotice}>
                  <Gift size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                  <span>🔥 Начни пользоваться сейчас — бесплатный доступ на 5 дней</span>
                </div>
              </>
            )}
          </div>

          <div style={styles.heroImage}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src="/images/hero-main.png" alt="HR LAMA Dashboard" style={styles.previewImage} />
              <div style={{ position: 'absolute', top: -40, left: -40, zIndex: 2 }}><SpinningLlama size={80} /></div>
            </div>
          </div>
        </section>

        {/* ===== STATS ===== */}
        <section style={styles.statsSection}>
          <div style={styles.statsGrid}>
            {stats.map((stat, idx) => (
              <div key={idx} style={styles.statCard}>
                <div style={styles.statIcon}>{stat.icon}</div>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== FEATURES ===== */}
        <section style={styles.featuresSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>6 рабочих контуров — в одной системе</h2>
            <p style={styles.sectionDesc}>Каждый модуль — законченный инструмент. Вместе — единая среда, где данные не теряются на стыках.</p>
          </div>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div key={index} style={styles.featureCard}>
                <div style={{ ...styles.featureIcon, backgroundColor: `${feature.color}15`, color: feature.color, boxShadow: `0 8px 25px ${feature.color}20` }}>{feature.icon}</div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
                <button onClick={feature.action} style={{ ...styles.featureLink, color: feature.color }}>Подробнее <ChevronRight size={16} /></button>
              </div>
            ))}
          </div>
        </section>

        {/* Система-шоукейс */}
        <SystemShowcase />

        {/* ===== PRICING ===== */}
        <section style={styles.pricingSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Тарифы</h2>
            <p style={styles.sectionDesc}>Каждый тариф — на <strong>3 пользователя</strong>. Все модули включены.</p>
          </div>
          <div style={styles.pricingCards}>
            <div style={styles.pricingCard}>
              <h3 style={styles.pricingCardTitle}>{pricing.monthly.label}</h3>
              <div style={styles.pricingValue}>{pricing.monthly.price.toLocaleString()} ₽</div>
              <div style={styles.pricingPeriod}>/ {pricing.monthly.period}</div>
              <div style={styles.pricingUserNote}>{pricing.monthly.description}</div>
              <ul style={styles.pricingFeatures}><li>✅ Все модули платформы</li><li>✅ Расширение для браузера (Chrome, Яндекс, Edge)</li><li>✅ Ежедневная поддержка</li><li>✅ Обновления и новые функции</li></ul>
              {!user && <button onClick={() => navigate('/register')} style={styles.pricingButton}>Попробовать</button>}
            </div>
            <div style={{ ...styles.pricingCard, borderColor: '#28a745', boxShadow: '0 25px 50px rgba(40,167,69,0.25)', transform: 'scale(1.03)' }}>
              <div style={styles.pricingBadge}><CheckCircle size={14} /> Популярно</div>
              <h3 style={styles.pricingCardTitle}>{pricing.quarterly.label}</h3>
              <div style={styles.pricingValue}>{pricing.quarterly.price.toLocaleString()} ₽</div>
              <div style={styles.pricingPeriod}>/ {pricing.quarterly.period}</div>
              <div style={styles.pricingUserNote}>{pricing.quarterly.description}</div>
              <ul style={styles.pricingFeatures}><li>✅ Все модули платформы</li><li>✅ Расширение для браузера (Chrome, Яндекс, Edge)</li><li>✅ Ежедневная поддержка</li><li>✅ Обновления и новые функции</li></ul>
              {!user && <button onClick={() => navigate('/register')} style={styles.pricingButtonPrimary}>Попробовать</button>}
            </div>
            <div style={{ ...styles.pricingCard, borderColor: '#6a11cb', boxShadow: '0 25px 50px rgba(106,17,203,0.25)' }}>
              <div style={styles.pricingBadge}>Выгодно</div>
              <h3 style={styles.pricingCardTitle}>{pricing.yearly.label}</h3>
              <div style={styles.pricingValue}>{pricing.yearly.price.toLocaleString()} ₽</div>
              <div style={styles.pricingPeriod}>/ {pricing.yearly.period}</div>
              <div style={styles.pricingSaving}>Экономия {pricing.yearly.saving.toLocaleString()} ₽</div>
              <div style={styles.pricingUserNote}>{pricing.yearly.description}</div>
              <ul style={styles.pricingFeatures}><li>✅ Все модули платформы</li><li>✅ Расширение для браузера (Chrome, Яндекс, Edge)</li><li>✅ Ежедневная поддержка</li><li>✅ Обновления и новые функции</li></ul>
              {!user && <button onClick={() => navigate('/register')} style={styles.pricingButtonPrimary}>Попробовать</button>}
            </div>
          </div>
        </section>

        {/* ===== IMPLEMENTATION ===== */}
        <section style={styles.implementationSection}>
          <div style={styles.implementationCard}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <h2 style={styles.implementationTitle}>Масштабирование</h2>
              <p style={styles.implementationDesc}>Платформа растёт вместе с вашим бизнесом — добавляйте отделы, филиалы и новых пользователей без ограничений.</p>
              <ul style={styles.implementationList}><li>Неограниченное количество пользователей</li><li>Мультифилиальная структура</li><li>Единая база знаний для всех сотрудников</li><li>Автоматическая синхронизация между отделами</li></ul>
              <button onClick={() => (window.location.href = 'mailto:info@hrlama.ru?subject=Запрос%20на%20внедрение')} style={styles.implementationButton}>Начать масштабирование</button>
            </div>
            <div style={styles.implementationIllustration}><SpinningLlama size={180} /></div>
          </div>
        </section>

        {/* ===== CHROME EXTENSION ===== */}
        <section style={styles.chromeSection}>
          <div style={styles.chromeCard}>
            <div style={styles.chromeIconWrapper}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 88, height: 88, borderRadius: 24, background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', boxShadow: '0 15px 40px rgba(37,117,252,0.4), 0 0 60px -10px rgba(106,17,203,0.5)', animation: 'hlfPuzzlePulse 3s ease-in-out infinite' }}>
                <Puzzle size={48} color="white" strokeWidth={1.8} />
              </div>
              <style>{`@keyframes hlfPuzzlePulse { 0%,100% { box-shadow: 0 15px 40px rgba(37,117,252,0.4), 0 0 60px -10px rgba(106,17,203,0.5); } 50% { box-shadow: 0 20px 50px rgba(37,117,252,0.55), 0 0 80px -10px rgba(106,17,203,0.7); } }`}</style>
            </div>
            <h2 style={styles.chromeTitle}>Расширение для браузера HR Лама</h2>
            <p style={styles.chromeDesc}>
              Сохраняйте резюме и вакансии с <b>hh.ru</b>, <b>superjob.ru</b> и <b>avito.ru</b> в один клик.
              Работает в <b>Chrome</b>, <b>Яндекс.Браузере</b> и <b>Edge</b>. Больше не нужно вручную
              копировать данные — расширение сделает всё за вас.
            </p>
            <button onClick={() => (user ? navigate('/dashboard') : navigate('/register'))} style={styles.chromeButton}>
              <Download size={18} style={{ marginRight: 8 }} /> Установить расширение
            </button>
          </div>
        </section>

        {/* ===== TESTIMONIALS ===== */}
        <section style={styles.testimonialsSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Что говорят клиенты</h2>
            <p style={styles.sectionDesc}>Нам доверяют ведущие компании</p>
          </div>
          <div style={styles.testimonialsGrid}>
            {testimonials.map((t, idx) => (
              <div key={idx} style={styles.testimonialCard}>
                <div style={styles.quoteIcon}>“</div>
                <p style={styles.testimonialText}>{t.text}</p>
                <div style={styles.testimonialAuthor}><strong>{t.name}</strong><span>{t.role}</span></div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== CTA ===== */}
        <section style={styles.ctaSection}>
          <div style={styles.ctaContent}>
            <h2 style={styles.ctaTitle}>Присоединяйтесь к компаниям, которые уже используют Лама HR</h2>
            <p style={styles.ctaDesc}>Автоматизируйте рекрутмент, анализируйте данные и развивайте команду с помощью единой платформы.</p>
            {!user && (
              <button onClick={() => navigate('/register')} style={styles.ctaButton}>
                Зарегистрироваться
              </button>
            )}
          </div>
        </section>

        <LeadFormSection />
      </main>

      {/* ===== FAB ===== */}
      <button
        type="button"
        onClick={() => setAppModalOpen(true)}
        style={styles.appFab}
        aria-label="Скачать мобильное приложение v15.0"
      >
        <img src="/mobile/icon.png" alt="" style={styles.appFabIcon} />
        <span style={styles.appFabBadge}>📱</span>
      </button>

      {/* ===== APP MODAL ===== */}
      
        {appModalOpen && (
          <div style={styles.appModalBackdrop} onClick={() => setAppModalOpen(false)}>
            <div style={styles.appModal} onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={() => setAppModalOpen(false)} style={styles.appModalClose} aria-label="Закрыть">×</button>
              <div style={styles.appModalIcon}><img src="/mobile/icon.png" alt="HR Lama" style={styles.appModalIconImg} /></div>
              <h3 style={styles.appModalTitle}>📱 HR Lama — мобильное приложение</h3>
              <p style={styles.appModalDesc}>Работайте с кандидатами, встречами и чатом — из любой точки</p>
              <div style={styles.appModalMeta}>
                <span style={styles.appModalMetaItem}>🤖 Android 8+</span>
                <span style={styles.appModalMetaItem}>📦 ~97 МБ</span>
                <span style={styles.appModalMetaItem}>🆓 Бесплатно</span>
                <span style={styles.appModalMetaItem}>✅ Версия 15.0</span>
              </div>
              <a href="/mobile/hr-lama.apk" download style={styles.appModalBtnPrimary} onClick={() => setAppModalOpen(false)}>📥 Скачать APK v15.0</a>
              <div style={styles.appModalQrWrap}>
                <div style={styles.appModalQrLabel}>Или отсканируйте QR-код:</div>
                <img src="/qr/apk.png?v=1312" alt="QR для скачивания" style={styles.appModalQr} />
              </div>
              <div style={styles.appModalHint}>⚠️ Перед установкой удалите старую версию приложения. Разрешите установку из неизвестных источников.</div>
            </div>
          </div>
        )}
      

      {/* ===== FOOTER ===== */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerLogo}><img src="/images/8.png" alt="Лама" style={styles.footerLogoIcon} /></div>
          <div style={styles.footerLinks}>
            <Link to="/about" style={styles.footerLink}>О нас</Link>
            <button onClick={() => setPrivacyOpen(true)} style={{ ...styles.footerLink, background: "none", border: "none", cursor: "pointer" }}>Политика конфиденциальности</button>
            <button onClick={() => setTermsOpen(true)} style={{ ...styles.footerLink, background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: "14px" }}>Пользовательское соглашение</button>
            <a href="mailto:info@hrlama.ru" style={styles.footerLink}>Поддержка: info@hrlama.ru</a>
          </div>
          <div style={styles.copyright}>© 2026 HR Лама. Все права защищены.</div>
        </div>
      </footer>

      <PrivacyPolicy isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <TermsOfService isOpen={termsOpen} onClose={() => setTermsOpen(false)} />
      <CookieConsent />
    </div>
  );
};

// ================= СТИЛИ =================
const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', margin: 0, padding: 0, background: 'linear-gradient(135deg, #f5f7fa 0%, #e6f0fa 100%)', fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif', position: 'relative', overflowX: 'hidden' },
  parallaxLayer: { position: 'fixed', borderRadius: '50%', zIndex: 0, pointerEvents: 'none', willChange: 'transform' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 32px', backgroundColor: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', position: 'sticky', top: 0, zIndex: 100, flexWrap: 'wrap', gap: '12px', transition: 'box-shadow 0.3s ease' },
  logoContainer: { display: 'flex', alignItems: 'center', gap: '12px' },
  logoIcon: { height: '40px', width: 'auto' },
  buttonPrimary: { background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: 'white', border: 'none', borderRadius: '40px', padding: '10px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginLeft: '12px', boxShadow: '0 8px 20px rgba(37,117,252,0.4)', transition: 'transform 0.2s, box-shadow 0.2s', whiteSpace: 'nowrap' },
  buttonSecondary: { background: 'transparent', color: '#2575fc', border: '2px solid #2575fc', borderRadius: '40px', padding: '8px 22px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginLeft: '12px', whiteSpace: 'nowrap' },
  main: { flex: 1, position: 'relative', zIndex: 1 },
  hero: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '60px 32px 40px', maxWidth: '1200px', margin: '0 auto', gap: '40px', flexWrap: 'wrap' },
  heroContent: { flex: '1 1 480px', minWidth: '320px', maxWidth: '620px' },
  heroBadge: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(106,17,203,0.08)', padding: '8px 18px', borderRadius: '30px', fontSize: '14px', color: '#6a11cb', fontWeight: 500, marginBottom: '24px', boxShadow: '0 4px 15px rgba(106,17,203,0.12)', border: '1px solid rgba(106,17,203,0.15)' },
  heroTitle: { fontSize: 'clamp(30px, 4.2vw, 52px)', fontWeight: 800, color: '#0f172a', marginBottom: '20px', lineHeight: 1.08, letterSpacing: '-0.03em' },
  gradientText: { background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSubtitle: { fontSize: 'clamp(15px, 1.5vw, 18px)', color: '#475569', lineHeight: 1.65, marginBottom: '32px', maxWidth: '540px' },
  heroButtons: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' },
  ctaButton: { background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: '50px', padding: '14px 32px', fontSize: '16px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 12px 30px rgba(37,117,252,0.4)', display: 'inline-flex', alignItems: 'center' },
  ctaOutlineButton: { background: 'transparent', color: '#2575fc', border: '2px solid #2575fc', borderRadius: '50px', padding: '12px 28px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' },
  demoNotice: { display: 'inline-flex', alignItems: 'center', background: 'rgba(37,117,252,0.08)', backdropFilter: 'blur(6px)', padding: '10px 18px', borderRadius: '40px', fontSize: '14px', fontWeight: 500, color: '#1a1a2e', border: '1px solid rgba(37,117,252,0.25)', gap: '8px' },
  heroImage: { flex: 1, minWidth: '280px', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  previewImage: { width: '100%', maxWidth: '560px', borderRadius: '24px', boxShadow: '0 30px 70px rgba(37,117,252,0.25)', border: '1px solid rgba(255,255,255,0.4)' },
  statsSection: { maxWidth: '1200px', margin: '40px auto', padding: '0 32px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' },
  statCard: { background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', padding: '24px 20px', borderRadius: '20px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s' },
  statIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(106,17,203,0.1), rgba(37,117,252,0.1))', color: '#2575fc', marginBottom: '12px' },
  statValue: { fontSize: '34px', fontWeight: 800, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '6px' },
  statLabel: { fontSize: '13px', color: '#64748b', lineHeight: 1.4 },
  featuresSection: { maxWidth: '1200px', margin: '80px auto 40px', padding: '0 32px' },
  sectionHeader: { textAlign: 'center', marginBottom: '48px' },
  sectionTitle: { fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 800, color: '#0f172a', marginBottom: '12px', letterSpacing: '-0.02em' },
  sectionDesc: { fontSize: '17px', color: '#64748b', maxWidth: '640px', margin: '0 auto', lineHeight: 1.6 },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' },
  featureCard: { background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', padding: '28px 24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', transition: 'box-shadow 0.3s, border-color 0.3s', cursor: 'pointer', display: 'flex', flexDirection: 'column' },
  featureIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '16px', marginBottom: '18px' },
  featureTitle: { fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' },
  featureDescription: { fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '16px', flex: 1 },
  featureLink: { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', fontWeight: 600, fontSize: '14px', cursor: 'pointer', padding: 0 },
  pricingSection: { maxWidth: '1200px', margin: '80px auto', padding: '0 32px' },
  pricingCards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', alignItems: 'stretch' },
  pricingCard: { background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', padding: '32px 28px', borderRadius: '24px', border: '2px solid transparent', boxShadow: '0 8px 30px rgba(0,0,0,0.06)', position: 'relative', display: 'flex', flexDirection: 'column' },
  pricingBadge: { position: 'absolute', top: -12, right: 24, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 4px 12px rgba(37,117,252,0.3)' },
  pricingCardTitle: { fontSize: '16px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' },
  pricingValue: { fontSize: '38px', fontWeight: 800, color: '#0f172a', marginBottom: '4px', letterSpacing: '-0.02em' },
  pricingPeriod: { fontSize: '15px', color: '#64748b', marginBottom: '12px' },
  pricingSaving: { display: 'inline-block', background: 'rgba(106,17,203,0.1)', color: '#6a11cb', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, marginBottom: '12px' },
  pricingUserNote: { fontSize: '13px', color: '#64748b', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid rgba(0,0,0,0.06)' },
  pricingFeatures: { listStyle: 'none', padding: 0, margin: '0 0 24px 0', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '10px' },
  pricingButton: { background: 'transparent', color: '#2575fc', border: '2px solid #2575fc', borderRadius: '12px', padding: '12px 24px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 'auto' },
  pricingButtonPrimary: { background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: '12px', padding: '14px 24px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 'auto', boxShadow: '0 8px 20px rgba(37,117,252,0.35)' },
  implementationSection: { maxWidth: '1200px', margin: '80px auto', padding: '0 32px' },
  implementationCard: { display: 'flex', alignItems: 'center', gap: '40px', flexWrap: 'wrap', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 20px 50px rgba(37,117,252,0.08)' },
  implementationTitle: { fontSize: 'clamp(26px, 3vw, 34px)', fontWeight: 800, color: '#0f172a', marginBottom: '12px' },
  implementationDesc: { fontSize: '16px', color: '#475569', lineHeight: 1.6, marginBottom: '20px' },
  implementationList: { listStyle: 'none', padding: 0, margin: '0 0 24px 0', display: 'flex', flexDirection: 'column', gap: '10px' },
  implementationButton: { background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: '40px', padding: '14px 32px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 10px 25px rgba(37,117,252,0.35)' },
  implementationIllustration: { flex: '0 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  chromeSection: { maxWidth: '860px', margin: '80px auto', padding: '0 32px' },
  chromeCard: { textAlign: 'center', background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(240,245,255,0.9))', backdropFilter: 'blur(12px)', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 20px 50px rgba(37,117,252,0.1)' },
  chromeIconWrapper: { marginBottom: '24px' },
  chromeTitle: { fontSize: 'clamp(22px, 2.5vw, 28px)', fontWeight: 800, color: '#0f172a', marginBottom: '16px' },
  chromeDesc: { fontSize: '16px', color: '#475569', lineHeight: 1.65, marginBottom: '28px', maxWidth: '640px', margin: '0 auto 28px' },
  chromeButton: { background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: '40px', padding: '14px 32px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(37,117,252,0.35)' },
  testimonialsSection: { maxWidth: '1200px', margin: '80px auto', padding: '0 32px' },
  testimonialsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' },
  testimonialCard: { background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', padding: '32px 28px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', position: 'relative' },
  quoteIcon: { fontSize: '64px', lineHeight: 1, color: 'rgba(106,17,203,0.2)', position: 'absolute', top: '10px', left: '20px', fontWeight: 800 },
  testimonialText: { fontSize: '15px', color: '#334155', lineHeight: 1.7, marginBottom: '20px', position: 'relative', zIndex: 1 },
  testimonialAuthor: { display: 'flex', flexDirection: 'column', gap: '2px', fontWeight: 600, color: '#0f172a', fontSize: '14px' },
  ctaSection: { maxWidth: '1200px', margin: '80px auto 0', padding: '0 32px' },
  ctaContent: { background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: 'white', padding: '64px 40px', borderRadius: '32px', textAlign: 'center', boxShadow: '0 30px 70px rgba(106,17,203,0.3)' },
  ctaTitle: { fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 800, marginBottom: '16px', letterSpacing: '-0.02em' },
  ctaDesc: { fontSize: '17px', opacity: 0.92, marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px', lineHeight: 1.6 },
  appFab: { position: 'fixed', bottom: '24px', right: '24px', width: '60px', height: '60px', borderRadius: '50%', background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', cursor: 'pointer', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  appFabIcon: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
  appFabBadge: { position: 'absolute', top: '-4px', right: '-4px', fontSize: '20px' },
  appModalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  appModal: { background: 'white', borderRadius: '24px', padding: '24px', maxWidth: '380px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', position: 'relative', textAlign: 'center' },
  appModalClose: { position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.05)', border: 'none', width: '36px', height: '36px', borderRadius: '50%', fontSize: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155' },
  appModalIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(106,17,203,0.1), rgba(37,117,252,0.1))', marginBottom: '12px' },
  appModalIconImg: { width: '48px', height: '48px', objectFit: 'contain', borderRadius: '10px' },
  appModalTitle: { fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' },
  appModalDesc: { fontSize: '13px', color: '#64748b', marginBottom: '12px', lineHeight: 1.4 },
  appModalMeta: { display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', marginBottom: '16px' },
  appModalMetaItem: { background: 'rgba(37,117,252,0.08)', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', color: '#2575fc', fontWeight: 600 },
  appModalBtnPrimary: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', textDecoration: 'none', padding: '12px 20px', borderRadius: '12px', fontWeight: 700, fontSize: '14px', boxShadow: '0 8px 20px rgba(37,117,252,0.3)', marginBottom: '16px' },
  appModalQrWrap: { padding: '12px', background: 'rgba(0,0,0,0.02)', borderRadius: '16px', marginBottom: '12px' },
  appModalQrLabel: { fontSize: '12px', color: '#64748b', marginBottom: '8px' },
  appModalQr: { width: '110px', height: '110px', margin: '0 auto', display: 'block' },
  appModalHint: { fontSize: '11px', color: '#94a3b8', lineHeight: 1.4, padding: '10px', background: 'rgba(255,193,7,0.08)', borderRadius: '10px' },
  footer: { background: '#ffffff', borderTop: '1px solid #e2e8f0', color: '#0f172a', padding: '48px 32px 32px', marginTop: '80px' },
  footerContent: { maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' },
  footerLogo: { marginBottom: '8px' },
  footerLogoIcon: { height: '44px', width: 'auto' },
  footerLinks: { display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'center', alignItems: 'center' },
  footerLink: { color: '#64748b', textDecoration: 'none', fontSize: '14px', transition: 'color 0.2s' },
  copyright: { fontSize: '13px', color: '#94a3b8', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', width: '100%', textAlign: 'center' },
};

export default LandingPage;
